<script generics="T extends ChainId" lang="ts">
	import Check from '@lucide/svelte/icons/check'
	import ChevronsUpDown from '@lucide/svelte/icons/chevrons-up-down'
	import { tick } from 'svelte'
	import { cn } from 'tailwind-variants'

	import { buttonVariants } from '#lib/components/ui/button/index.js'
	import * as Command from '#lib/components/ui/command/index.js'
	import * as Popover from '#lib/components/ui/popover/index.js'
	import type { ChainId } from '#lib/models/index.js'

	type Option = {
		value: T
		label: string
		subLabel?: string
		disabled?: boolean
		slug: string
	}

	let {
		value: valueProps = $bindable(),
		class: className = null,
		placeholder,
		disabled = false,
		options,
		onChange = null,
	}: {
		placeholder: string
		disabled?: boolean
		options: Option[]
		class?: Nullable<string>
		value?: Nullable<T | T[]>
		onChange?: Nullable<(value: Nullable<T | T[]>) => void>
	} = $props()

	let open = $state(false)
	let valueState = $state('')
	let triggerRef = $state<Nullable<HTMLButtonElement>>(null)

	const selectedValue = $derived(
		Array.isArray(valueProps)
			? options.filter(f => valueProps?.includes(f.value)).map(f => f.value)
			: options.find(f => f.value === valueProps)?.value
	)

	// We want to refocus the trigger button when the user selects
	// an item from the list so users can continue navigating the
	// rest of the form with the keyboard.
	const closeAndFocusTrigger = () => {
		open = false

		tick().then(() => {
			triggerRef?.focus()
		})
	}
</script>

<Popover.Root bind:open>
	<Popover.Trigger
		class={cn(buttonVariants({ variant: 'outline' }), className)}
		aria-expanded={open}
		{disabled}
		role="combobox"
		bind:ref={triggerRef}
	>
		{#if Array.isArray(selectedValue)}
			{placeholder}
		{:else}
			{const option = $derived(options.find(({ value }) => value === selectedValue))}

			{#if option}
				<img
					class="size-4.5"
					alt="{option.label} logo"
					crossorigin="anonymous"
					decoding="async"
					fetchpriority="low"
					height="18"
					loading="eager"
					src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{option.slug}.svg"
					width="18"
				/>

				{option.label}

				{#if option.subLabel}
					<span class="text-primary/60">
						{option.subLabel}
					</span>
				{/if}
			{:else}
				{placeholder}
			{/if}
		{/if}

		<ChevronsUpDown class="ml-auto h-4 w-4 shrink-0 opacity-50" />
	</Popover.Trigger>

	<Popover.Portal>
		<Popover.Content
			class="w-(--bits-popover-anchor-width) min-w-(--bits-popover-anchor-width) overflow-auto p-0"
			preventScroll={false}
			side="bottom"
		>
			<Command.Root>
				<Command.Input {placeholder} />

				<Command.List>
					<Command.Loading />
					<Command.Empty>Oops, nothing found...</Command.Empty>
					<Command.Group>
						{#each options as { value, label, subLabel, disabled = false, slug } (value)}
							<Command.Item
								{disabled}
								onSelect={() => {
									valueState = value

									if (Array.isArray(valueProps)) {
										// @ts-expect-error types mismatch
										if (valueProps.includes(valueState)) {
											valueProps = valueProps.filter(v => v !== valueState)
										} else {
											// @ts-expect-error types mismatch
											valueProps = [...valueProps, valueState]
										}
									} else {
										// @ts-expect-error types mismatch
										valueProps = valueState
										closeAndFocusTrigger()
									}

									onChange?.(valueProps)
								}}
								{value}
							>
								{#if Array.isArray(valueProps) ? valueProps.includes(value) : valueProps === value}
									<Check size="18" />
								{:else}
									<img
										class="size-4.5"
										alt="{label} logo"
										crossorigin="anonymous"
										decoding="async"
										fetchpriority="low"
										height="18"
										loading="eager"
										src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{slug}.svg"
										width="18"
									/>
								{/if}

								{label}

								{#if subLabel}
									<span class="text-primary/60 ml-auto self-end-safe">
										{subLabel}
									</span>
								{/if}
							</Command.Item>
						{/each}
					</Command.Group>
				</Command.List>
			</Command.Root>
		</Popover.Content>
	</Popover.Portal>
</Popover.Root>
